import React, { useEffect, useState } from 'react';
import { css, cx } from '@linaria/atomic';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import { theme } from '@frontend/theme';
import { EmojiIcon } from '../../../icon';
import { PopoverDialog, usePopoverDialog } from '../../popover';
import { ActionDropdownChip, ActionPressedChip } from '../atoms';

const EMOJI_PICKER_WIDTH = 350;
const EMOJI_PICKER_HEIGHT = 450;

const emojiPickerActionEmojiIconStyle1 = css`
  min-width: ${theme.spacing(2.5)};
  padding: ${theme.spacing(0.25, 0)};
`;

const emojiPickerActionPopoverDialogStyle2 = css`
  width: ${EMOJI_PICKER_WIDTH}px;
  max-height: ${EMOJI_PICKER_HEIGHT};
  padding: 0;
  border-radius: ${theme.borderRadius.medium};
  max-width: calc(100vw - ${theme.spacing(2)});
`;

const emojiPickerActionDivStyle3 = css`
  width: ${EMOJI_PICKER_WIDTH}px;
  max-width: calc(100vw - ${theme.spacing(2)});
  height: ${EMOJI_PICKER_HEIGHT}px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const emojiPickerActionEmojiPickerStyle4 = css`
  font-family: ${theme.font.family};
`;

type EmojiPickerActionProps = {
  onSelect: (selection: EmojiClickData) => void;
  showCaret?: boolean;
} & Pick<React.ComponentProps<typeof ActionPressedChip>, 'showBoxShadow' | 'compact' | 'className' | 'isActive'>;

export const EmojiPickerAction = ({
  showBoxShadow = true,
  compact = true,
  isActive,
  onSelect,
  showCaret,
  ...rest
}: EmojiPickerActionProps) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const {
    getDialogProps: getEmojiDialogProps,
    getTriggerProps: getEmojiTriggerProps,
    isOpen: emojiDialogIsOpen,
    close: closeEmojiDialog,
  } = usePopoverDialog({
    placement: 'bottom-start',
    middlewareOptions: {
      flip: {
        crossAxis: true,
        padding: 8,
      },
      shift: {
        crossAxis: true,
        padding: 8,
      },
    },
  });

  const handleSelect = (emoji: EmojiClickData) => {
    onSelect(emoji);
    setShowEmojiPicker(false);
    closeEmojiDialog();
  };

  useEffect(() => {
    let id: ReturnType<typeof setTimeout>;

    if (!emojiDialogIsOpen) {
      setShowEmojiPicker(false);
    } else {
      id = setTimeout(() => setShowEmojiPicker(true), 400);
    }

    return () => {
      if (id) {
        clearTimeout(id);
      }
    };
  }, [emojiDialogIsOpen]);

  return (
    <>
      <ActionDropdownChip
        showBoxShadow={showBoxShadow}
        isOpen={emojiDialogIsOpen}
        isActive={emojiDialogIsOpen}
        compact={compact}
        showCaret={showCaret}
        {...getEmojiTriggerProps()}
        {...rest}
      >
        <EmojiIcon size={24} className={emojiPickerActionEmojiIconStyle1} />
      </ActionDropdownChip>
      <PopoverDialog {...getEmojiDialogProps()} className={emojiPickerActionPopoverDialogStyle2} returnFocus={false}>
        <div className={cx(emojiPickerActionDivStyle3, emojiPickerActionEmojiPickerStyle4)}>
          {showEmojiPicker && (
            <EmojiPicker
              onEmojiClick={handleSelect}
              lazyLoadEmojis
              height={EMOJI_PICKER_HEIGHT}
              width={EMOJI_PICKER_WIDTH}
            />
          )}
        </div>
      </PopoverDialog>
    </>
  );
};
